import * as AlertDialog from '@radix-ui/react-alert-dialog';
import { useState } from 'react';
import s from './CancelOnboardingAlert.module.scss';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onboardingId: string;
  contractorName: string;
  onCancelled: () => void;
}

export function CancelOnboardingAlert({
  open,
  onOpenChange,
  onboardingId,
  contractorName,
  onCancelled,
}: Props) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleConfirm(): Promise<void> {
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`/api/onboardings/${onboardingId}/cancel`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok && res.status !== 204) {
        throw new Error(`${res.status} ${res.statusText}`);
      }
      onCancelled();
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AlertDialog.Root open={open} onOpenChange={onOpenChange}>
      <AlertDialog.Portal>
        <AlertDialog.Overlay className={s.overlay} />
        <AlertDialog.Content className={s.content}>
          <AlertDialog.Title className={s.title}>Cancel this onboarding?</AlertDialog.Title>
          <AlertDialog.Description className={s.description}>
            The magic link for <strong>{contractorName}</strong> will stop working. They will not be
            able to submit. You can start a new onboarding for them later if needed.
          </AlertDialog.Description>

          {error && (
            <div role="alert" className={s.alertError}>
              Could not cancel: {error}
            </div>
          )}

          <div className={s.actions}>
            <AlertDialog.Cancel asChild>
              <button type="button" className={s.cancel} disabled={submitting}>
                Keep onboarding
              </button>
            </AlertDialog.Cancel>
            <button
              type="button"
              className={s.confirm}
              onClick={() => {
                void handleConfirm();
              }}
              disabled={submitting}
            >
              {submitting ? 'Cancelling…' : 'Cancel onboarding'}
            </button>
          </div>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}
